import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Smile, Activity } from 'lucide-react';
import type { User } from '@supabase/auth-js';
import { useEntity } from '../core/resolvers/useEntity';
import { useUIStore } from '../stores/uiStore';
import { IdentityRenderer } from '../core/identity/IdentityRenderer';
import { LinkEmbed } from './LinkEmbed';
import type { Message, NeuralMessageBubbleProps } from '../types/chat';

export const NeuralMessageBubble = ({
  msg,
  isSent,
  readReceipts,
  activeVault,
  onReaction,
  onMoreReactions,
  onUserClick,
  currentUser,
  senderFontStyle,
  roleColor
}: NeuralMessageBubbleProps) => {
  const { setViewingImageUrl, setViewingProfileId } = useUIStore();
  const { entity: resolvedUser } = useEntity(msg.senderId || null, 'user');

  const displayUser = resolvedUser || {
    id: msg.senderId || 'unknown',
    name: msg.senderName || 'Unknown',
    avatar: msg.senderAvatar
  };

  const isJumbo = /^([\p{Extended_Pictographic}\s]|!\[[^\]]*\]\([^)]*\))+$/u.test(msg.text);

  return (
    <div className={`msg-wrap group`} id={`msg-${msg.id}`}>
      <div className={`flex gap-3`}>
        <div 
          className="w-8 h-8 rounded-full overflow-hidden shrink-0 cursor-pointer mt-1 border border-[var(--stroke)] shadow-lg hover:scale-110 transition-transform"
          onClick={() => setViewingProfileId(msg.senderId === currentUser?.id ? 'me' : msg.senderId || '')}
          onContextMenu={(e) => {
            e.preventDefault();
            onUserClick(e, msg.senderId === currentUser?.id ? 'me' : msg.senderId || '');
          }}
        >
          <img 
            src={displayUser.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'} 
            className="w-full h-full object-cover" 
            style={{ objectPosition: displayUser.customization?.avatarPosition || '50% 50%' }}
            referrerPolicy="no-referrer" 
          />
        </div>
        <div className="relative">
          <div className={`flex items-center gap-2 mb-1`}>
            <IdentityRenderer 
               user={displayUser}
               containerClassName="flex items-center gap-1.5"
               nameClassName="text-[10px] font-bold text-[var(--text)] opacity-70"
               pronounClassName="text-[8px] font-semibold uppercase tracking-wider opacity-50"
               roleColor={roleColor}
            />
            <span className="text-[8px] text-[var(--muted)] opacity-50">{msg.time}</span>
          </div>
          <div className={`msg-bubble ${isJumbo ? 'jumbo' : ''}`}>
            {msg.decryptionFailed ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-[11px] font-mono" role="status" aria-live="polite">
                <span aria-hidden="true">🔒</span>
                <span>Could not decrypt this message — the local vault key did not match.</span>
              </div>
            ) : null}
            <div className={`markdown-body ${isJumbo ? 'flex flex-wrap gap-1 items-center' : ''}`}>
              <ReactMarkdown
                components={{
                  p: ({ node, children }: any) => {
                    if (isJumbo) {
                      return <span>{children}</span>;
                    }
                    return <p>{children}</p>;
                  },
                  a: ({ node, href, children, ...props }: any) => {
                    if (href?.startsWith('mention://')) {
                      return <span className="mention-pill text-[var(--neon)] font-bold cursor-pointer hover:underline">{children}</span>;
                    }
                    return <a href={href} {...props}>{children}</a>;
                  },
                  img: ({ node, ...props }: any) => {
                    const isVideo = props.alt === 'video';
                    const isAudio = props.alt === 'audio';
                    
                    if (isVideo) {
                      return (
                        <div className="my-2 rounded-xl overflow-hidden border border-white/10 bg-black shadow-xl">
                          <video src={props.src} controls className="max-w-full max-h-[300px]" />
                        </div>
                      );
                    }
                    
                    if (isAudio) {
                      return (
                        <div className="my-2 p-2 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3">
                          <audio src={props.src} controls className="w-full h-8 accent-[var(--neon)]" />
                        </div>
                      );
                    }
                    
                    return (
                      <div 
                        className="my-2 rounded-xl overflow-hidden shadow-2xl w-full cursor-pointer"
                        onClick={(e) => { 
                          e.preventDefault(); 
                          e.stopPropagation(); 
                          
                          setViewingImageUrl(props.src); 
                        }}
                      >
                        <img {...props} className="w-full h-auto max-h-[500px] object-contain hover:scale-[1.01] transition-transform" />
                      </div>
                    );
                  }
                }}
              >
                {msg.text.replace(/@(\w+)/g, '[@$1](mention://$1)')}
              </ReactMarkdown>
            </div>
            
            {msg.expiresAt && (
              <div className="flex items-center gap-1 mt-2 text-[8px] font-mono text-[var(--meta-color)] opacity-70">
                <Activity size={10} className="animate-pulse" />
                {Math.max(0, Math.floor((msg.expiresAt - Date.now()) / 1000))}s
              </div>
            )}

            {isSent && !activeVault && (
              <div className="absolute -bottom-4 right-0 text-[8px] text-[var(--muted)] opacity-30">
                {Object.values(readReceipts).includes(msg.id) ? 'READ' : 'SENT'}
              </div>
            )}
          </div>

          {/* Render Link Embeds for any URLs in the text */}
          {(() => {
            const urls = msg.text.match(/(https?:\/\/[^\s]+)/g);
            if (!urls) return null;
            // Clean markdown syntax from URLs and filter out image/media links
            const uniqueUrls = Array.from(new Set(urls))
              .map(url => url.replace(/[)\]}>]+$/, ''))
              .filter(url => !url.match(/\.(jpeg|jpg|gif|png|webp|mp4|webm)$/i));
            if (uniqueUrls.length === 0) return null;
            return (
              <div className="flex flex-col gap-2 mt-2">
                {uniqueUrls.map((url, i) => (
                  <LinkEmbed key={i} url={url} />
                ))}
              </div>
            );
          })()}
          
          {/* Message Actions */}
          <div className={`absolute top-0 -right-24 opacity-0 group-hover:opacity-100 transition-all flex gap-1.5 bg-black/60 backdrop-blur-md rounded-lg p-1.5 z-10 border border-[var(--stroke)] shadow-2xl scale-90 group-hover:scale-100 origin-center`}>
             <button onClick={() => onReaction(msg.id, '👍')} className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded transition-colors text-lg">👍</button>
             <button onClick={() => onReaction(msg.id, '🔥')} className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded transition-colors text-lg">🔥</button>
             <button onClick={() => onMoreReactions(msg.id)} className="w-8 h-8 flex items-center justify-center hover:bg-white/10 hover:text-[var(--neon)] rounded transition-colors"><Smile size={18} /></button>
          </div>
        </div>
      </div>
      
      {msg.reactions && Object.keys(msg.reactions).length > 0 && (
        <div className={`flex gap-1 mt-1 ml-11`}>
          {Object.entries(msg.reactions).map(([emoji, count]: any) => (
            <span key={emoji} className="text-[11px] bg-white/5 px-2 py-0.5 rounded-full border border-[var(--stroke)] flex items-center gap-1">
              {emoji} <span className="text-[var(--muted)]">{count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
